import React from "react";
import { Provider } from "react-redux";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import styled from "styled-components/macro";
import GlobalStyle from "./styles/GlobalStyle";
import store from "./app/store";
import queryClient from "./shared/queryClient";
import AnalyticsLoader from "./features/analytics/AnalyticsLoader";
import PageNotFound from "./pages/PageNotFound";
import QuoteSelector from "./pages/Quote/QuoteSelector";
import SignupOtherClientsFlow from "./pages/SignupOtherClients/SignupOtherClientsFlow";
import MoreInformationFlow from "./pages/MoreInformation/MoreInformationFlow";
import TermsAndConditionsPage from "./pages/Quote/TermsAndConditionsPage";
import ReferrerTerms from "./pages/ReferrerQuote/ReferrerTerms";
import RedirectToStripeCheckout from "./pages/payments/RedirectToStripeCheckout";
import SourceOfFundsFlow from "./pages/SourceOfFunds/SourceOfFundsFlow";
import RemoSignupFlow from "./pages/RemortgageSignup/RemoSignupFlow";
import RemoLeaseholdFlow from "./pages/RemortgageLeasehold/RemoLeaseholdFlow";
import Login from "./pages/ClientDashboard/Login";
import Logout from "./pages/ClientDashboard/Logout";
import LoginCode from "./pages/ClientDashboard/LoginCode";
import LinkExpired from "./pages/ClientDashboard/LinkExpired";
import SharedOwnershipPurchaseFlow from "./pages/SharedOwnershipPurchase/SharedOwnershipPurchaseFlow";
import DashboardSelector from "./pages/ClientDashboard/DashboardSelector";
import SignupFlow from "./pages/Signup/SignupFlow";
import ContinueSignup from "./pages/ContinueSignup/ContinueSignup";
import ReferrerQuoteFlow from "./pages/ReferrerQuote/ReferrerQuoteFlow";
import TA10Flow from "./pages/PropertyInformationFormTA10/TA10Flow";
import BrokerQuoteFlow from "./pages/BrokerQuote/BrokerQuoteFlow";
import RedirectOldReferAClientRoute from "./pages/ReferrerQuote/RedirectOldReferAClientRoute";
import AnonymousRequestFlow from "./pages/AnonymousRequest/AnonymousRequestFlow";
import ReferAFriendPage from "./pages/ReferAFriend/ReferAFriendPage";
import LazyRoute from "./LazyRoute";
import { isProbateSignupRouteEnabled } from "./pages/ProbateSignup/probateSignupRoute";

const IdentityCheckFlow = React.lazy(() => import("./pages/IdentityCheck/IdentityCheckFlow"));
const EsignatureRequestFlow = React.lazy(() => import("./pages/EsignatureRequest/EsignatureRequestFlow"));
const StudioIdentityCheckFlow = React.lazy(() => import("./pages/IdentityCheck/StudioIdentityCheckFlow"));
const ProbateSignupFlow = React.lazy(() => import("./pages/ProbateSignup/ProbateSignupFlow"));

const AppDiv = styled.div`
  width: 100%;
  height: 100%;
`;

function RedirectToJuno() {
  window.location.replace("https://www.juno.legal/");
  return null;
}

function App() {
  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <AnalyticsLoader />
        <Router>
          <AppDiv>
            <GlobalStyle />
            <Routes>
              <Route path="/" element={<RedirectToJuno />} />
              <Route path="/quote/:referralCode" element={<QuoteSelector />} />
              <Route path="/quote" element={<QuoteSelector />} />
              <Route path="/remortgage/:signupId" element={<RemoSignupFlow />} />
              <Route path="/refer-a-client/*" element={<RedirectOldReferAClientRoute />} />
              <Route path="/refer-a-friend/:referralCode" element={<ReferAFriendPage />} />
              <Route path="/refer/terms" element={<ReferrerTerms />} />
              <Route path="/refer/broker/:referralCode" element={<BrokerQuoteFlow />} />
              <Route path="/refer/*" element={<ReferrerQuoteFlow />} />
              <Route path="/signup/:signupId" element={<SignupFlow />} />
              <Route path="/continue-signup" element={<ContinueSignup />} />
              {/*<Route path="/signup/:signupId" element={<SignupTemporarilySuspended/> } />*/}
              <Route path="/client/:clientId" element={<SignupOtherClientsFlow />} />
              <Route path="/requests/more-information/:requestId" element={<MoreInformationFlow />} />
              <Route path="/requests/remortgage-leasehold-information/:requestId" element={<RemoLeaseholdFlow />} />
              <Route path="/requests/source-of-funds/:requestId" element={<SourceOfFundsFlow />} />
              <Route path="/requests/shared-ownership-purchase/:requestId" element={<SharedOwnershipPurchaseFlow />} />
              <Route
                path="/requests/identity-check/:requestId"
                element={
                  <LazyRoute>
                    <IdentityCheckFlow />
                  </LazyRoute>
                }
              />
              <Route
                path="/requests/studio-identity-check/:requestId"
                element={
                  <LazyRoute>
                    <StudioIdentityCheckFlow />
                  </LazyRoute>
                }
              />
              <Route
                path="/requests/esignature-signing-request/:requestId"
                element={
                  <LazyRoute>
                    <EsignatureRequestFlow />
                  </LazyRoute>
                }
              />
              <Route path="/requests/property-information-form-TA10/:requestId" element={<TA10Flow />} />
              <Route path="/requests/information-request/:doableId" element={<AnonymousRequestFlow />} />
              <Route path="/requests/document-request/:doableId" element={<AnonymousRequestFlow />} />
              {/* The flow loads on demand, so an unreleased feature stays out of
                    the main bundle. With an id is where the quote email's
                    "Get started" button lands. */}
              {isProbateSignupRouteEnabled() ? (
                <Route
                  path="/probate/signup/:signupId?"
                  element={
                    <LazyRoute>
                      <ProbateSignupFlow />
                    </LazyRoute>
                  }
                />
              ) : null}
              <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
              <Route path="/payment/:success?" element={<RedirectToStripeCheckout />} />
              <Route path="/login/code/:loginId" element={<LoginCode />} />
              <Route path="/login" element={<Login />} />
              <Route path="/logout" element={<Logout />} />
              <Route path="/link-expired" element={<LinkExpired />} />
              <Route path="/dashboard/*" element={<DashboardSelector />} />
              <Route element={<PageNotFound />} />
            </Routes>
          </AppDiv>
        </Router>
      </QueryClientProvider>
    </Provider>
  );
}

export default App;
